import React, { useState } from "react";
import {
  Dialog,
  Slide,
  Grid,
  CardMedia,
  Typography,
  TextField as TF,
  Button,
  Autocomplete,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { size, COLORS } from "../../utils";
import space from "../../assets/lottiefiles/space.json";
import Lottie from "react-lottie";

const useStyles = makeStyles(() => ({
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      borderWidth: size({ p: 0 }),
    },
  },
  cssFocused: {
    borderWidth: size({ p: 0 }),
  },
  notchedOutline: {
    borderWidth: size({ p: 0 }),
  },
}));

function TextField({ label, tf_props, params }) {
  const cls = useStyles();
  params = params ?? {};

  return (
    <Grid item container direction="column">
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Space Mono",
            fontSize: size({ p: 1 }),
            fontWeight: 400,
            wordSpacing: size({ p: 0.2, neg: true }),
            marginBottom: size({ p: 0.5 }),
            color: `${COLORS.WHITE}A0`,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <TF
          placeholder={label}
          {...tf_props}
          {...params}
          InputProps={{
            style: {
              fontFamily: "Space Mono",
              color: COLORS.WHITE,
              backgroundColor: "#242424",
              fontSize: size({ p: 1 }),
            },
            classes: {
              root: cls.cssOutlinedInput,
              focused: cls.cssFocused,
              notchedOutline: cls.notchedOutline,
            },
            ...params.InputProps,
          }}
        />
      </Grid>
    </Grid>
  );
}

export default function Enquire({ open, setOpen, data }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    projectCode: null,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.contact || !formData.projectCode) {
      setMessage("Please fill all fields.");
      return;
    }

    setLoading(true);
    setMessage("");

    const payload = {
      subject: "New Project Enquiry",
      body: `Name: ${formData.name}\nContact: ${formData.contact}\nProject Code: ${formData.projectCode.Code}`,
      base64_data: null,
      filename: null,
    };

    try {
      const res = await fetch("http://api.thinkgrail.com/genesysemailapi/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setMessage("✅ Thank you! We'll be in touch shortly.");
        setFormData({ name: "", contact: "", projectCode: null });
      } else {
        const errorData = await res.json();
        setMessage(
          errorData.message || "❌ Submission failed. Please try again."
        );
      }
    } catch (error) {
      setMessage("⚠️ Something went wrong. Try again later.");
    }

    setLoading(false);
  };

  return (
    <Dialog open={open} TransitionComponent={Slide} fullScreen>
      <div
        style={{
          width: isMobile ? "100%" : size({ p: 100 }),
          height: isMobile ? "100%" : size({ p: 100, h: true }),
          backgroundColor: COLORS.WHITE,
          backgroundImage: `
            linear-gradient(${COLORS.C5}40 ${size({
            p: 0.06,
          })}, transparent ${size({ p: 0.06 })}),
            linear-gradient(to right, ${COLORS.C5}40 ${size({ p: 0.06 })}, ${
            COLORS.WHITE
          } ${size({ p: 0.06 })})
          `,
          backgroundSize: `${size({ p: 2.5 })} ${size({ p: 2 })}`,
          position: "relative",
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : undefined,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Lottie Animation */}
        <div
          style={{
            position: "absolute",
            left: isMobile ? "1%" : size({ p: 50 }),
            top: isMobile ? "5%" : undefined,
          }}
        >
          <Lottie
            width={isMobile ? size({ p: 60 }) : size({ p: 40 })}
            height={isMobile ? size({ p: 60 }) : size({ p: 40 })}
            style={{ ...(isMobile && { marginLeft: size({ p: 20 }) }) }}
            options={{
              loop: true,
              autoplay: true,
              animationData: space,
            }}
          />
        </div>

        {/* Form */}
        <div
          style={{
            marginLeft: isMobile ? 0 : size({ p: -38 }),
            position: "relative",
            marginTop: isMobile ? size({ p: 45 }) : 0,
            zIndex: 1,
          }}
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="start"
            flexWrap="nowrap"
            rowGap={size({ p: 1.5 })}
            sx={{
              backgroundColor: COLORS.C5,
              borderRadius: isMobile ? size({ p: 1.5 }) : size({ p: 0.3 }),
              width: isMobile ? size({ p: 85 }) : size({ p: 38 }),
              height: isMobile
                ? size({ p: 40, h: true })
                : size({ p: 80, h: true }),
              padding: size({ p: 2 }),
            }}
          >
            <Typography
              onClick={() => setOpen(false)}
              sx={{
                fontFamily: "Space Mono",
                fontSize: isMobile ? size({ p: 5 }) : size({ p: 3 }),
                color: COLORS.WHITE,
                position: "absolute",
                cursor: "pointer",
                right: size({ p: 2 }),
                top: size({ p: 1 }),
              }}
            >
              x
            </Typography>

            {/* Header */}
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              rowGap={size({ p: 0.5 })}
            >
              <CardMedia
                component="img"
                image="/assets/images/projects/enquiry.png"
                sx={{
                  width: isMobile ? size({ p: 9 }) : size({ p: 7 }),
                  height: isMobile ? size({ p: 9 }) : size({ p: 7 }),
                  marginTop: isMobile ? "20px" : 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "Space Mono",
                  fontSize: isMobile ? size({ p: 3.5 }) : size({ p: 1.8 }),
                  fontWeight: 600,
                  color: COLORS.WHITE,
                  textAlign: "center",
                }}
              >
                Project Enquiry
              </Typography>
            </Grid>

            {/* Fields */}
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              rowGap={size({ p: 1 })}
            >
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 70 }) : size({ p: 26 }) }}
              >
                <TextField
                  tf_props={{
                    placeholder: "Name",
                    name: "name",
                    value: formData.name,
                    onChange: handleChange,
                    fullWidth: true,
                  }}
                />
              </Grid>
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 70 }) : size({ p: 26 }) }}
              >
                <TextField
                  tf_props={{
                    placeholder: "Contact Number",
                    name: "contact",
                    value: formData.contact,
                    onChange: handleChange,
                    fullWidth: true,
                  }}
                />
              </Grid>
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 70 }) : size({ p: 26 }) }}
              >
                <Autocomplete
                  options={data}
                  value={formData.projectCode}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, projectCode: newValue }))
                  }
                  getOptionLabel={(option) => option.Code || ""}
                  renderInput={(params) => (
                    <TextField
                      params={params}
                      tf_props={{
                        placeholder: "Project Code",
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Submit & Feedback */}
            <Grid
              item
              container
              direction="column"
              alignItems="center"
              rowGap={size({ p: 0.3 })}
            >
              <Grid
                item
                sx={{ width: isMobile ? size({ p: 60 }) : size({ p: 26 }) }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
                    backgroundColor: COLORS.C6,
                    fontFamily: "Archia",
                    fontWeight: 400,
                    fontSize: isMobile ? size({ p: 2.3 }) : size({ p: 1 }),
                    color: COLORS.WHITE,
                    textTransform: "none",
                    ":hover": { backgroundColor: COLORS.C6 },
                    marginTop: isMobile ? "5px" : 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {loading && (
                    <CircularProgress
                      size={isMobile ? size({ p: 2 }) : 18}
                      color="inherit"
                      style={{
                        marginRight: "1rem",
                      }}
                    />
                  )}
                  Enquire Now
                </Button>
              </Grid>

              {message && (
                <Grid
                  item
                  sx={{ width: isMobile ? size({ p: 60 }) : size({ p: 26 }) }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Space Mono",
                      fontSize: size({ p: 0.7 }),
                      fontWeight: 400,
                      color: `${COLORS.WHITE}A0`,
                      textAlign: "center",
                    }}
                  >
                    {message}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    </Dialog>
  );
}
